// Audit log viewer — owner only.
//
// Shows the most recent N entries from `auditLogs`, with optional filters
// by entity (product / transaction / return / event_stock_allocation / user)
// and action prefix.
//
// Audit logs are append-only and written by every mutating API path. This
// view is the canonical "what changed and who did it" surface for owners.

import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Segmented } from '@/components/ui/Segmented';
import { supabase } from '@/lib/supabase';
import { ScrollText, Filter, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { SearchInput } from '@/components/ui/SearchInput';
import { Select } from '@/components/ui/Select';
import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingState } from '@/components/ui/LoadingState';
import { useAuditLogs } from '@/hooks/useAuditLogs';
import { formatDateTime, formatRelative, cn } from '@/lib/format';
import type { AuditLogEntry } from '@/lib/api/auditLogs';

const ENTITY_OPTIONS: { value: 'all' | AuditLogEntry['entity']; label: string }[] = [
  { value: 'all', label: 'All entities' },
  { value: 'product', label: 'Product' },
  { value: 'transaction', label: 'Transaction' },
  { value: 'return', label: 'Return' },
  { value: 'event_stock_allocation', label: 'Event stock' },
  { value: 'user', label: 'User' },
];

const ENTITY_VARIANT: Record<string, 'success' | 'warning' | 'info' | 'danger' | 'default'> = {
  product: 'info',
  transaction: 'success',
  return: 'warning',
  event_stock_allocation: 'default',
  user: 'default',
};

function AuditTrail({ tabs }: { tabs: ReactNode }) {
  const [entityFilter, setEntityFilter] =
    useState<(typeof ENTITY_OPTIONS)[number]['value']>('all');
  const [search, setSearch] = useState('');

  const { data: logs, isLoading, refetch, isFetching } = useAuditLogs({
    limit: 500,
  });

  const filtered = useMemo(() => {
    const list = logs ?? [];
    let next = list;
    if (entityFilter !== 'all') {
      next = next.filter((l) => l.entity === entityFilter);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      next = next.filter((l) => {
        if (l.action.toLowerCase().includes(q)) return true;
        if (l.entityId.toLowerCase().includes(q)) return true;
        if (l.userDisplayName?.toLowerCase().includes(q)) return true;
        if (l.userEmail?.toLowerCase().includes(q)) return true;
        if (l.reason?.toLowerCase().includes(q)) return true;
        return false;
      });
    }
    return next;
  }, [logs, entityFilter, search]);

  return (
    <>
      <PageHeader
        overline="Owner only"
        title="Audit logs"
        description="Append-only trail of every mutation — product edits, allocations, transactions, fulfillment, returns, user changes."
        actions={
          <Button
            variant="secondary"
            size="md"
            onClick={() => refetch()}
            loading={isFetching}
            leftIcon={<RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} />}
          >
            Refresh
          </Button>
        }
      />
      {tabs}

      <Card padding="md" className="mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 min-w-0">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Cari action / entity id / user / reason…"
            />
          </div>
          <div className="sm:w-56">
            <Select
              value={entityFilter}
              onChange={(value) =>
                setEntityFilter(value as (typeof ENTITY_OPTIONS)[number]['value'])
              }
              aria-label="Filter by entity"
            >
              {ENTITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <p className="text-caption text-ink-subtle mt-3">
          Showing {filtered.length} dari {logs?.length ?? 0} entries (newest first).
        </p>
      </Card>

      <Card padding="none">
        {isLoading ? (
          <LoadingState message="Loading audit logs…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<ScrollText size={24} strokeWidth={1.5} />}
            title={logs?.length === 0 ? 'Belum ada audit log' : 'Tidak ada hasil'}
            description={
              logs?.length === 0
                ? 'Logs akan muncul setelah ada mutasi pertama.'
                : 'Coba ubah filter atau kata kunci pencarian.'
            }
          />
        ) : (
          <ul className="divide-y divide-line">
            {filtered.map((entry) => (
              <AuditRow key={entry.id} entry={entry} />
            ))}
          </ul>
        )}
      </Card>

      {!isLoading && filtered.length > 0 && (
        <p className="text-caption text-ink-subtle text-center mt-4">
          <Filter size={11} className="inline-block align-middle mr-1" />
          Logs lebih lama tersedia via query langsung ke <code>auditLogs</code>.
        </p>
      )}
    </>
  );
}

function AuditRow({ entry }: { entry: AuditLogEntry }) {
  const [expanded, setExpanded] = useState(false);
  const userLabel =
    entry.userDisplayName ??
    entry.userEmail ??
    entry.createdBy ??
    entry.userId ??
    '(system)';

  const hasDetails = entry.before || entry.after || entry.reason;

  return (
    <li className="px-4 sm:px-6 py-3 hover:bg-canvas/50 transition-colors">
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={() => hasDetails && setExpanded((v) => !v)}
          disabled={!hasDetails}
          className={cn(
            'mt-0.5 shrink-0 w-5 h-5 flex items-center justify-center text-ink-subtle',
            hasDetails ? 'cursor-pointer hover:text-ink' : 'cursor-default opacity-0',
          )}
          aria-label={expanded ? 'Collapse details' : 'Expand details'}
          aria-expanded={expanded}
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <code className="text-caption font-mono font-medium text-ink">
              {entry.action}
            </code>
            <Chip variant={ENTITY_VARIANT[entry.entity] ?? 'default'}>
              {entry.entity}
            </Chip>
            <span className="text-caption text-ink-subtle">
              <span title={formatDateTime(entry.createdAt)}>
                {formatRelative(entry.createdAt)}
              </span>
            </span>
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-ink-subtle">
            <span>
              by <span className="font-medium text-ink">{userLabel}</span>
            </span>
            <span>
              target:{' '}
              <code className="font-mono text-ink">{entry.entityId.slice(0, 16)}</code>
              {entry.entityId.length > 16 && '…'}
            </span>
          </div>

          {expanded && (
            <div className="mt-3 space-y-2 text-caption">
              {entry.reason && (
                <div>
                  <span className="text-ink-subtle uppercase text-overline tracking-wider mr-2">
                    Reason
                  </span>
                  <span className="text-ink">{entry.reason}</span>
                </div>
              )}
              {entry.before && (
                <details className="border border-line rounded p-2 bg-canvas/30">
                  <summary className="cursor-pointer text-ink-subtle">
                    Before
                  </summary>
                  <pre className="text-caption font-mono text-ink-subtle whitespace-pre-wrap break-all mt-1">
                    {JSON.stringify(entry.before, null, 2)}
                  </pre>
                </details>
              )}
              {entry.after && (
                <details className="border border-line rounded p-2 bg-canvas/30">
                  <summary className="cursor-pointer text-ink-subtle">
                    After
                  </summary>
                  <pre className="text-caption font-mono text-ink-subtle whitespace-pre-wrap break-all mt-1">
                    {JSON.stringify(entry.after, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

// ── Error aplikasi ──────────────────────────────────────────────────────────
// What failed in users' browsers (migration 0041): crashes and failed saves,
// with the message the user saw. The server logs cannot show these — most
// never reached the server.

interface ClientErrorRow {
  id: number;
  created_at: string;
  user_email: string | null;
  kind: string;
  message: string;
  stack: string | null;
  route: string | null;
  context: Record<string, unknown> | null;
  app_version: string | null;
  user_agent: string | null;
}

const KIND_LABEL: Record<string, string> = {
  render: 'Halaman crash',
  runtime: 'Error script',
  promise: 'Error script',
  mutation: 'Gagal simpan',
  query: 'Gagal memuat',
};

function ClientErrors({ tabs }: { tabs: ReactNode }) {
  const [openId, setOpenId] = useState<number | null>(null);
  const { data, isLoading, refetch, isFetching, error } = useQuery({
    queryKey: ['client-errors'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('client_errors' as never)
        .select('*')
        .order('created_at', { ascending: false })
        .limit(300);
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as ClientErrorRow[];
    },
  });

  return (
    <>
      <PageHeader
        overline="Owner only"
        title="Audit logs"
        description="Error yang dialami pengguna di browser: halaman crash dan simpan yang gagal, lengkap dengan pesan yang mereka lihat."
        actions={
          <Button variant="secondary" size="md" onClick={() => refetch()} loading={isFetching}
            leftIcon={<RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} />}>
            Refresh
          </Button>
        }
      />
      {tabs}
      {isLoading ? (
        <LoadingState message="Memuat error…" />
      ) : error ? (
        <Card><p className="text-small text-danger">{(error as Error).message}</p></Card>
      ) : !data || data.length === 0 ? (
        <Card>
          <EmptyState icon={<ScrollText size={24} strokeWidth={1.5} />} title="Belum ada error tercatat"
            description="Kalau pengguna mengalami error atau gagal menyimpan, catatannya muncul di sini." />
        </Card>
      ) : (
        <Card padding="none">
          <ul className="divide-y divide-line">
            {data.map((e) => {
              const open = openId === e.id;
              return (
                <li key={e.id} className="px-4 py-3">
                  <button type="button" onClick={() => setOpenId(open ? null : e.id)} className="w-full text-left flex items-start gap-3">
                    {open ? <ChevronDown size={16} className="mt-0.5 text-ink-subtle shrink-0" /> : <ChevronRight size={16} className="mt-0.5 text-ink-subtle shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Chip variant={e.kind === 'mutation' || e.kind === 'render' ? 'danger' : 'warning'}>{KIND_LABEL[e.kind] ?? e.kind}</Chip>
                        <span className="text-caption text-ink-subtle">{formatDateTime(new Date(e.created_at).getTime())} · {formatRelative(new Date(e.created_at).getTime())}</span>
                        <span className="text-caption text-ink-subtle">{e.user_email ?? '—'}</span>
                        <code className="text-caption text-ink-subtle">{e.route}</code>
                      </div>
                      <p className="text-small text-ink mt-1 break-words">{e.message}</p>
                    </div>
                  </button>
                  {open && (
                    <pre className="mt-2 ml-7 p-3 rounded bg-canvas border border-line text-[11px] leading-snug text-ink-subtle overflow-x-auto whitespace-pre-wrap break-words max-h-80">
{JSON.stringify({ context: e.context, version: e.app_version, browser: e.user_agent }, null, 2)}
{e.stack ? `\n${e.stack}` : ''}
                    </pre>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </>
  );
}

export default function AuditLogs() {
  const [tab, setTab] = useState<'audit' | 'errors'>('audit');
  const tabs = (
    <div className="mb-4">
      <Segmented<'audit' | 'errors'>
        value={tab}
        onChange={setTab}
        role="tabs"
        aria-label="Bagian audit"
        options={[
          { value: 'audit', label: 'Audit trail' },
          { value: 'errors', label: 'Error aplikasi' },
        ]}
      />
    </div>
  );
  return tab === 'audit' ? <AuditTrail tabs={tabs} /> : <ClientErrors tabs={tabs} />;
}
